import { View, StyleSheet, ScrollView } from "react-native";
import WebPage from "@/ui/WebPage";
import Heading from "@/ui/Heading";
import LargeButton from "@/ui/LargeButton";
import { useTranslation } from "react-i18next";
import { ms } from "@/ui/moderateScale";
import { Colors } from "@/constants/Colors";
import { useAuth } from "@/models/useAuth";
import { useUserSlice } from "@/models/useUserSlice";
import { Clipboard } from "@capacitor/clipboard";
import toast from "react-hot-toast";
import { gotoPrivacy, gotoTerms, suggestFeature } from "@/models/utils";
import { useEffect } from "react";
import { useUISlice } from "@/models/useUISlice";
import { getPlatform } from "@/models/platform";

export default function Settings() {
  const { t } = useTranslation();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const userData = useUserSlice((state) => state.userData);
  const isLoggedIn = useAuth.isAuthenticated();
  const isMobile = useUISlice((state) => state.device.isMobile);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const _contactSupport = () => {
    const callback = () => {
      const subject = t("screens.settings.contactSupportSubject");
      const body = t("screens.settings.contactSupportBody");

      window.open(
        `mailto:sadeck@eznote.ai?subject=${subject}&body=${body}`,
        "_blank"
      );
    };

    if (!useAuth.isAuthenticated()) {
      useAuth.signIn({});

      return;
    }

    callback();
  };

  const _shareWithFriend = async () => {
    Clipboard.write({ string: "https://eznote.ai" }).then(() => {
      toast.success(t("screens.settings.linkCopied"));
    });
  };

  const _copyEmail = async () => {
    Clipboard.write({ string: userData?.email }).then(() => {
      toast.success(t("screens.settings.emailCopied"));
    });
  };

  const _deleteAccount = () => {
    const subject = "Delete account";
    const body = "Please delete my account";

    window.open(
      `mailto:sadeck@eznote.ai?subject=${subject}&body=${body}`,
      "_blank"
    );
  };

  const _billingPortal = () => {
    useUserSlice.getState().subscription().stripeBillingPortal();
  };

  const _logout = () => {
    useAuth.signOut();
  };

  return (
    <WebPage tab="settings">
      <ScrollView style={{ flex: 1, paddingBottom: ms(100) }}>
        <Heading
          style={isMobile ? { fontSize: ms(24), marginTop: ms(20) } : {}}
          text={t("screens.settings.title")}
        />

        <View style={styles.spacer} />
        {isLoggedIn && (
          <LargeButton
            text={userData?.email}
            onPress={_copyEmail}
            hideRightIcon
          />
        )}

        <View style={styles.spacer} />
        {isSubscribed && getPlatform() === "web" && (
          <LargeButton
            text={t("screens.settings.manageSubscription")}
            onPress={_billingPortal}
          />
        )}

        <View style={styles.spacer} />
        <LargeButton
          text={t("screens.settings.contactSupport")}
          onPress={_contactSupport}
        />

        <View style={styles.spacer} />
        <LargeButton
          text={t("screens.settings.suggestFeature")}
          onPress={suggestFeature}
        />

        <View style={styles.spacer} />
        <LargeButton
          text={t("screens.settings.shareWithFriend")}
          onPress={_shareWithFriend}
        />

        <View style={styles.spacer} />
        <LargeButton
          text={t("screens.settings.privacyPolicy")}
          onPress={gotoPrivacy}
        />

        <View style={styles.spacer} />
        <LargeButton
          text={t("screens.settings.termsOfService")}
          onPress={gotoTerms}
        />

        {isLoggedIn && (
          <>
            <View style={styles.spacer} />
            <LargeButton
              text={t("screens.settings.logout")}
              onPress={_logout}
              textStyle={{ color: Colors.error }}
            />

            <View style={styles.spacer} />
            <LargeButton
              text={t("screens.settings.deleteAccount")}
              onPress={_deleteAccount}
              textStyle={{ color: Colors.error }}
            />
          </>
        )}
      </ScrollView>
    </WebPage>
  );
}

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
});
